<template>
  <label
    class="label"
    :class="{
      mini: props.mini,
      disabled: disabled,
      white: white,
      grey: grey,
    }"
  >
    <input
      class="checkbox"
      v-if="type === 'checkbox'"
      type="checkbox"
      :checked="modelValue as boolean"
      @input="handleInput"
      :disabled="disabled"
    />
    <input
      class="checkbox"
      v-else
      type="radio"
      :checked="modelValue === value"
      :value="value"
      @click="handleChange"
      :disabled="disabled"
    />
    <span class="fake" :class="{ _radio: type === 'radio' }">
      <i class="icon"></i>
    </span>
    <span class="text" v-if="props.text" :class="{ border: border }">
      {{ text }}
    </span>
  </label>
</template>

<script setup lang="ts">
import { defineProps, defineEmits } from "vue";

type BaseProps = {
  white?: boolean;
  grey?: boolean;
};

type CheckboxProps = BaseProps & {
  modelValue: boolean;
  value?: string | number;
  text?: string;
  type: "checkbox";
  border?: boolean;
  mini?: boolean;
  disabled?: boolean;
};

type RadioProps = BaseProps & {
  modelValue: string | null | number;
  value: string;
  text?: string;
  type: "radio";
  border?: boolean;
  mini?: boolean;
  disabled?: boolean;
};

type Props = CheckboxProps | RadioProps;

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean | string): void;
}>();

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit("update:modelValue", target.checked);
};

const handleChange = () => {
  if (props.type === "radio" && props.value !== undefined) {
    emit("update:modelValue", props.value);
  }
};
</script>

<style lang="scss" scoped>
.label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  height: fit-content;
  background: var(--color-border);
  min-width: 26px;
  min-height: 26px;
  border-radius: 7px;
  cursor: pointer;
  width: fit-content;
  &.disabled {
    background-color: var(--color-bg-layout);
    cursor: not-allowed;
  }
  &.mini {
    min-width: 20px;
    min-height: 20px;
    & .fake {
      min-width: 10px;
      min-height: 10px;
    }
  }
  &.grey {
    & .fake {
      background: var(--color-border);
    }
  }
  &:hover {
    & .fake {
    }
  }
}

.checkbox {
  display: none;
}

.fake {
  display: block;
  min-width: 12px;
  min-height: 12px;
  position: relative;
  transition: 0.2s;
  border-radius: 4px;
  background: transparent;
  &._radio {
    border-radius: 50%;
  }
  & .icon {
    position: absolute;
    display: block;
    transform: translate(-50%, -50%);
    opacity: 0;
    transition: 0.2s;
    top: 50%;
    left: 50%;
    font-size: 9px;
    color: #fff;
    line-height: 0;
    background: #00a554;
  }
}

.checkbox:checked + .fake {
  background: #00a554;
  border-color: transparent;
}

.checkbox:checked + .fake .icon {
  opacity: 1;
}

.text {
  transition: 0.2s;
  font-size: 12px;
  flex: 1;
  &.border {
    border-bottom: 1px solid #ebebeb;
  }
}
</style>
